import { useState, useCallback, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, orderBy, query, limit as firestoreLimit } from 'firebase/firestore';
import { db, functions } from '../lib/firebase/config';

export interface ReportUpdate {
  id: string;
  userId: string;
  images: Array<{
    url: string;
    altText?: string;
    orderIndex: number;
  }>;
  tags: string[];
  comment?: string;
  createdAt: any;
  flagged?: boolean;
  flagReason?: string;
}

export interface AddUpdateData {
  reportId: string;
  comment?: string;
  tags?: string[];
  imageUrls?: string[];
}

export const useReportUpdates = (reportId?: string) => {
  const [updates, setUpdates] = useState<ReportUpdate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoize Firebase Functions
  const addReportUpdateFunction = useMemo(() => httpsCallable(functions, 'addReportUpdate'), []);
  const getReportUpdatesFunction = useMemo(() => httpsCallable(functions, 'getReportUpdates'), []);
  const markReportAsGoneFunction = useMemo(() => httpsCallable(functions, 'markReportAsGone'), []);

  // Real-time listener for updates
  useEffect(() => {
    if (!reportId) {
      setUpdates([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    const updatesRef = collection(db, 'reports', reportId, 'updates');
    const updatesQuery = query(
      updatesRef,
      orderBy('createdAt', 'desc'),
      firestoreLimit(50)
    );

    const unsubscribe = onSnapshot(
      updatesQuery,
      (snapshot) => {
        const updatesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as ReportUpdate[];
        
        setUpdates(updatesData);
        setIsLoading(false);
      },
      (error) => {
        console.error('Error listening to updates:', error);
        setError('Failed to load updates');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [reportId]);

  const addUpdate = useCallback(async (data: AddUpdateData): Promise<boolean> => {
    if (!data.reportId) {
      setError('Report ID is required');
      return false;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await addReportUpdateFunction(data);
      
      if ((result.data as any)?.success) {
        // The real-time listener will automatically update the updates list
        return true;
      } else {
        setError('Failed to add update');
        return false;
      }
    } catch (error) {
      console.error('Error adding update:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add update';
      setError(errorMessage);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [addReportUpdateFunction]);

  const markAsGone = useCallback(async (reportId: string, comment?: string): Promise<boolean> => {
    if (!reportId) {
      setError('Report ID is required');
      return false;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await markReportAsGoneFunction({ reportId, comment });
      
      if ((result.data as any)?.success) {
        return true;
      } else {
        setError('Failed to mark as gone');
        return false;
      }
    } catch (error) {
      console.error('Error marking as gone:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to mark as gone';
      setError(errorMessage);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [markReportAsGoneFunction]);

  const fetchUpdates = useCallback(async (reportId: string, limit: number = 20): Promise<ReportUpdate[]> => {
    try {
      const result = await getReportUpdatesFunction({ reportId, limit });
      return (result.data as any)?.updates || [];
    } catch (error) {
      console.error('Error fetching updates:', error);
      setError('Failed to fetch updates');
      return [];
    }
  }, [getReportUpdatesFunction]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    updates,
    isLoading,
    error,
    isSubmitting,
    addUpdate,
    markAsGone,
    fetchUpdates,
    clearError
  };
};